import { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import { useQueryFilters } from '../../../lib/hooks';
import { SPORTCENTER_DISCOUNTS } from '../../../lib/queryKeys';
import { AppContext } from '../../../sporttia/AppContext';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import useScsService from '../../../services/ScsService';
import type { Discount } from '../../../types/models/Discount';
import { GetScsDiscountsParams } from '../../../types/api/paths/Scs';
import { SttButtonFab, SttPage } from '../../../sporttia/all';
import DiscountEditDialog from './DiscountEditDialog';
import { DefaultListParams } from '../../../types/api/utils';
import { useDiscountsTour } from 'src/lib/tours/useDiscountsTour';
import { PERMISSIONS } from '../../../lib/permissions';

export default function Discounts() {
    const { t } = useTranslation();
    const { startTour } = useDiscountsTour();

    const cxt = useContext(AppContext)!;

    // Set page header title
    useEffect(() => {
        cxt.setHeader({ title: t('generic.discounts') });
    }, []);

    const [selectedDiscount, setSelectedDiscount] = useState<Discount>();
    const [isDiscountEditDialogOpen, setIsDiscountEditDialogOpen] =
        useState(false);

    const scsService = useScsService();

    const [filters, setFilters] = useQueryFilters<GetScsDiscountsParams>(
        {},
        SPORTCENTER_DISCOUNTS,
    );

    const queryClient = useQueryClient();
    const queryParams = useMemo(
        () => ({ ...filters, id: cxt.sc!.id }),
        [filters, cxt.sc],
    );
    const queryKey = useMemo(
        () => [SPORTCENTER_DISCOUNTS, queryParams],
        [queryParams],
    );

    const openDiscount = useCallback((discount: Discount) => {
        setSelectedDiscount(discount);
        setIsDiscountEditDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDiscountEditDialogOpen(false);
        setSelectedDiscount(undefined);
    }, []);

    const handleDialogSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey });
        setSelectedDiscount(undefined);
        setIsDiscountEditDialogOpen(false);
    }, [queryClient, queryKey]);

    const createDiscount = useCallback(() => {
        setIsDiscountEditDialogOpen(true);
    }, []);

    const handleFilter = useCallback(
        (value: GetScsDiscountsParams) => {
            setFilters(value);
        },
        [setFilters],
    );

    const queryFn = useCallback(
        ({ id, ...params }: typeof queryParams & DefaultListParams) =>
            scsService.getScsDiscounts(id, params),
        [scsService],
    );

    const columns = useMemo(
        () => [
            {
                title: t('Name'),
                field: 'name',
                align: 'left' as const,
                value: (row: Discount) => (
                    <Link
                        sx={{
                            color: 'var(--brand-text)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => openDiscount(row)}
                    >
                        {row.name}
                    </Link>
                ),
            },
            {
                title: t('discountPercentage'),
                align: 'right' as const,
                value: (row: Discount) => `${row.discountPercentage}%`,
            },
        ],
        [t, openDiscount],
    );

    return (
        <SttPage title={t('generic.discounts')} help={startTour}>
            <SttTopFilteringControls
                p={2}
                fields={[
                    {
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                        type: 'text',
                        caption: t('Name'),
                        name: 'name',
                    },
                ]}
                onFilter={handleFilter}
            />

            <SttCachedTable
                queryKey={SPORTCENTER_DISCOUNTS}
                queryFn={queryFn}
                queryParams={queryParams}
                prefetching
                columns={columns}
            />

            {isDiscountEditDialogOpen && (
                <DiscountEditDialog
                    discount={selectedDiscount}
                    onClose={closeDialog}
                    onUpdate={handleDialogSuccess}
                    onSave={handleDialogSuccess}
                    onDelete={handleDialogSuccess}
                />
            )}

            <SttButtonFab
                onClick={createDiscount}
                permission={PERMISSIONS.DISCOUNT.CREATE}
            />
        </SttPage>
    );
}
